/**
 * Connection audit list (read-only)
 *
 * Login / logout activity of the tenant's users. The backend scopes the rows
 * to the caller's organization(s) (roles 4/5) or returns all for admin, so
 * this view carries no client-side authorization: it only presents the data.
 *
 * Datetimes are stored without a timezone; render them as-is (no browser
 * shift), matching the call-history view.
 */

import { useMemo, useState } from 'react';
import { List, TextField, FunctionField, useTranslate } from 'react-admin';
import { CustomDatagrid, CrudListActions } from '@/components/common';
import { compactListSx } from '@/components/common/compactListSx';
import {
  CollapsibleFilterPanel,
  FilterExpandContext,
} from '@/components/common/CollapsibleFilterPanel';
import { CustomTextInputFilter } from '@/components/common/CustomFilters';

/** Render a stored datetime as-is (no browser timezone shift) */
const formatStoredDateTime = (value: unknown): string => {
  const iso = String(value ?? '');
  if (iso.length < 16) return '';
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)} ${iso.slice(11, 16)}`;
};

export const ConnectionList = () => {
  const translate = useTranslate();
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const filterExpandValue = useMemo(
    () => ({ expanded: filtersExpanded, setExpanded: setFiltersExpanded }),
    [filtersExpanded]
  );

  const filters = useMemo(
    () => [
      <CollapsibleFilterPanel key="filters" source="_cfp" alwaysOn alwaysVisibleCount={0}>
        <CustomTextInputFilter
          source="q"
          label={translate('common.filters.search', { _: 'Buscar' })}
          alwaysOn
        />
      </CollapsibleFilterPanel>,
    ],
    [translate]
  );

  return (
    <FilterExpandContext.Provider value={filterExpandValue}>
      <List
        sx={compactListSx}
        filters={filters}
        actions={
          <CrudListActions
            showCreate={false}
            exportColumns={['user_name', 'user_email', 'login_connection_date', 'logout_connection_date']}
          />
        }
        sort={{ field: 'date', order: 'DESC' }}
        exporter={false}
      >
        <CustomDatagrid
          primarySource="user_name"
          secondarySource="user_email"
          bulkActionButtons={false}
          rowClick={false}
        >
          <TextField source="user_name" sortable={false} />
          <TextField source="user_email" sortable={false} />
          <FunctionField
            source="login_connection_date"
            render={(record: any) => formatStoredDateTime(record?.login_connection_date)}
            sortable={false}
          />
          <TextField source="login_connection_device_os" sortable={false} />
          <FunctionField
            source="logout_connection_date"
            render={(record: any) => formatStoredDateTime(record?.logout_connection_date)}
            sortable={false}
          />
        </CustomDatagrid>
      </List>
    </FilterExpandContext.Provider>
  );
};

export default ConnectionList;
